import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Plus, Trash2, User, Building2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Logo, SiteTag } from "@/components/Logo";
import { useSiteInfo } from "@/hooks/useSiteInfo";

const HERO_IMG =
    process.env.REACT_APP_HERO_IMAGE_URL ||
    "https://customer-assets.emergentagent.com/job_carwash-booking-55/artifacts/j6ttbbsy_499995035_1495626971825096_5759970648268719768_n.jpg";

const STEPS = [
    { id: 1, label: "Voor wie?" },
    { id: 2, label: "Klantgegevens" },
    { id: 3, label: "Wagens" },
    { id: 4, label: "Overzicht" },
];

const blankVehicle = () => ({
    service_type: "exterior",
    date: "",
    slot: "",
    license_plate: "",
});

export default function Book() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const site = useSiteInfo();
    const [step, setStep] = useState(1);
    const [services, setServices] = useState([]);
    const [days, setDays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [form, setForm] = useState({
        customer_type: "",
        customer_name: "",
        customer_email: "",
        vat_number: "",
        vehicles: [blankVehicle()],
    });

    useEffect(() => {
        if (params.get("cancelled")) {
            toast.error("Boeking geannuleerd.");
        }
    }, [params]);

    // Pre-fill the form from a previous group booking (?repeat=<group_id>)
    useEffect(() => {
        const repeatId = params.get("repeat");
        if (!repeatId) return;
        api.get(`/bookings/group/${repeatId}`)
            .then(({ data }) => {
                if (!data || !Array.isArray(data.bookings) || data.bookings.length === 0) {
                    return;
                }
                const first = data.bookings[0];
                setForm({
                    customer_type: first.customer_type || "private",
                    customer_name: first.customer_name || "",
                    customer_email: first.customer_email || "",
                    vat_number: first.vat_number || "",
                    vehicles: data.bookings.map((b) => ({
                        service_type: b.service_type,
                        date: "",
                        slot: "",
                        license_plate: b.license_plate || "",
                    })),
                });
                // Skip the "voor wie?" + customer steps — straight to vehicles.
                setStep(3);
                toast.success(
                    `${data.bookings.length} wagen${data.bookings.length === 1 ? "" : "s"} voorgevuld — kies een nieuwe datum en tijdslot.`,
                );
            })
            .catch(() => toast.error("Vorige boeking niet gevonden — start opnieuw."));
    }, [params]);

    useEffect(() => {
        Promise.all([api.get("/services"), api.get("/available-days")])
            .then(([s, d]) => {
                setServices(s.data);
                setDays(d.data);
            })
            .catch(() => toast.error("Kon de boekingsgegevens niet laden"))
            .finally(() => setLoading(false));
    }, []);

    const serviceById = useMemo(
        () => Object.fromEntries(services.map((s) => [s.id, s])),
        [services],
    );

    const canNext = () => {
        if (step === 1) return Boolean(form.customer_type);
        if (step === 2) {
            const baseOk =
                form.customer_name.trim().length >= 2 &&
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customer_email.trim());
            if (form.customer_type === "business") {
                return baseOk && form.vat_number.trim().length >= 4;
            }
            return baseOk;
        }
        if (step === 3) {
            return form.vehicles.every(
                (v) =>
                    v.service_type &&
                    v.date &&
                    v.slot &&
                    v.license_plate.trim().length >= 4,
            );
        }
        return true;
    };

    const next = () => setStep((s) => Math.min(4, s + 1));
    const back = () => setStep((s) => Math.max(1, s - 1));

    const updateVehicle = (i, patch) =>
        setForm((f) => ({
            ...f,
            vehicles: f.vehicles.map((v, idx) =>
                idx === i ? { ...v, ...patch } : v,
            ),
        }));

    const addVehicle = () =>
        setForm((f) => ({ ...f, vehicles: [...f.vehicles, blankVehicle()] }));

    const removeVehicle = (i) =>
        setForm((f) => ({
            ...f,
            vehicles: f.vehicles.filter((_, idx) => idx !== i),
        }));

    const total = useMemo(
        () =>
            form.vehicles.reduce(
                (s, v) =>
                    s + (serviceById[v.service_type]?.price_eur || 0),
                0,
            ),
        [form.vehicles, serviceById],
    );

    const submit = async () => {
        setSubmitting(true);
        try {
            const res = await api.post("/bookings", {
                customer_type: form.customer_type,
                customer_name: form.customer_name.trim(),
                customer_email: form.customer_email.trim(),
                vat_number:
                    form.customer_type === "business"
                        ? form.vat_number.trim()
                        : null,
                vehicles: form.vehicles.map((v) => ({
                    service_type: v.service_type,
                    date: v.date,
                    slot: v.slot,
                    license_plate: v.license_plate.trim(),
                })),
            });
            navigate(`/book/success?group_id=${res.data.group_id}`);
        } catch (e) {
            const msg =
                e?.response?.data?.detail || "Boeking kon niet aangemaakt worden";
            toast.error(msg);
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-12">
            <aside className="lg:col-span-5 bg-[#0A1F44] text-white relative overflow-hidden">
                <img
                    src={HERO_IMG}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-25"
                />
                <div className="relative z-10 p-8 md:p-12 lg:p-16 h-full flex flex-col">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm"
                        data-testid="back-home-link"
                    >
                        <ArrowLeft size={16} /> Terug naar home
                    </Link>

                    <div className="mt-12">
                        <Logo tone="light" subtitle="GROUP" />
                        <div className="mt-4">
                            <SiteTag name={site.name} city={site.city} tone="light" />
                        </div>
                    </div>

                    <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter mt-10 leading-[1.02]">
                        Boek je wasbeurt
                        <br />
                        op {site.name}.
                    </h1>
                    <p className="text-white/60 mt-4 text-sm">
                        Eén of meerdere wagens in 4 snelle stappen. Factuur volgt
                        achteraf.
                    </p>

                    <div className="mt-12 space-y-5">
                        {STEPS.map((s) => (
                            <div
                                key={s.id}
                                className="flex items-center gap-4"
                                data-testid={`stepper-${s.id}`}
                            >
                                <div
                                    className={`w-8 h-8 flex items-center justify-center font-mono-ui text-xs border ${
                                        step === s.id
                                            ? "bg-[#ED6A14] border-[#ED6A14] text-white"
                                            : step > s.id
                                              ? "bg-white border-white text-[#0A1F44]"
                                              : "border-white/30 text-white/40"
                                    }`}
                                >
                                    {step > s.id ? <Check size={14} /> : String(s.id).padStart(2, "0")}
                                </div>
                                <span
                                    className={`text-sm ${step >= s.id ? "text-white" : "text-white/40"}`}
                                >
                                    {s.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </aside>

            <main className="lg:col-span-7 p-8 md:p-12 lg:p-16">
                <div className="max-w-3xl">
                    <p className="text-sm uppercase tracking-widest text-[#555]">
                        Stap {String(step).padStart(2, "0")} /{" "}
                        {String(STEPS.length).padStart(2, "0")}
                    </p>
                    <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mt-2 text-[#0A1F44]">
                        {STEPS[step - 1].label}
                    </h2>

                    {loading ? (
                        <div
                            className="mt-12 flex items-center gap-3 text-[#555]"
                            data-testid="loading-state"
                        >
                            <Loader2 className="animate-spin" size={18} /> Laden...
                        </div>
                    ) : (
                        <div key={step} className="step-enter mt-10">
                            {step === 1 && (
                                <CustomerTypeStep
                                    value={form.customer_type}
                                    onChange={(v) =>
                                        setForm((f) => ({ ...f, customer_type: v }))
                                    }
                                />
                            )}
                            {step === 2 && (
                                <CustomerDetailsStep
                                    form={form}
                                    setForm={setForm}
                                />
                            )}
                            {step === 3 && (
                                <VehiclesStep
                                    vehicles={form.vehicles}
                                    days={days}
                                    services={services}
                                    updateVehicle={updateVehicle}
                                    addVehicle={addVehicle}
                                    removeVehicle={removeVehicle}
                                />
                            )}
                            {step === 4 && (
                                <SummaryStep
                                    form={form}
                                    services={services}
                                    total={total}
                                />
                            )}
                        </div>
                    )}

                    <div className="mt-16 flex items-center justify-between border-t border-[#e5e5e5] pt-6">
                        {step > 1 ? (
                            <button
                                onClick={back}
                                className="inline-flex items-center gap-2 text-[#555] hover:text-[#0A1F44] transition-colors text-sm font-semibold"
                                data-testid="step-back-btn"
                            >
                                <ArrowLeft size={16} /> Terug
                            </button>
                        ) : (
                            <button
                                onClick={() => navigate("/")}
                                className="inline-flex items-center gap-2 text-[#555] hover:text-[#0A1F44] transition-colors text-sm font-semibold"
                                data-testid="cancel-btn"
                            >
                                <ArrowLeft size={16} /> Annuleren
                            </button>
                        )}
                        {step < 4 ? (
                            <button
                                onClick={next}
                                disabled={!canNext()}
                                className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-7 py-3.5 font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="step-next-btn"
                            >
                                Volgende <ArrowRight size={16} />
                            </button>
                        ) : (
                            <button
                                onClick={submit}
                                disabled={submitting}
                                className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-7 py-3.5 font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-60"
                                data-testid="confirm-booking-btn"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="animate-spin" size={16} /> Boeken...
                                    </>
                                ) : (
                                    <>
                                        Bevestig boeking <ArrowRight size={16} />
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}

function CustomerTypeStep({ value, onChange }) {
    const options = [
        {
            id: "private",
            icon: User,
            title: "Particulier",
            desc: "Voor jezelf of een privé-wagen. Naam, e-mail en nummerplaat volstaan.",
        },
        {
            id: "business",
            icon: Building2,
            title: "Bedrijf",
            desc: "Voor één of meerdere bedrijfswagens. Btw-nummer is verplicht voor facturatie.",
        },
    ];
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {options.map((o) => {
                const active = value === o.id;
                const Icon = o.icon;
                return (
                    <button
                        key={o.id}
                        onClick={() => onChange(o.id)}
                        className={`text-left p-8 border transition-colors ${
                            active
                                ? "border-[#ED6A14] bg-[#ED6A14]/[0.05]"
                                : "border-[#e5e5e5] hover:border-[#0A1F44]"
                        }`}
                        data-testid={`customer-type-${o.id}`}
                    >
                        <Icon className="text-[#ED6A14]" size={28} />
                        <h3 className="font-display font-bold text-2xl tracking-tight mt-4 text-[#0A1F44]">
                            {o.title}
                        </h3>
                        <p className="text-sm text-[#555] mt-2 leading-relaxed">{o.desc}</p>
                    </button>
                );
            })}
        </div>
    );
}

function CustomerDetailsStep({ form, setForm }) {
    const isBusiness = form.customer_type === "business";
    return (
        <div className="space-y-10 max-w-lg">
            <div>
                <label className="text-xs uppercase tracking-widest text-[#555]">
                    {isBusiness ? "Bedrijfsnaam" : "Voornaam + Achternaam"}
                </label>
                <input
                    value={form.customer_name}
                    onChange={(e) =>
                        setForm((f) => ({ ...f, customer_name: e.target.value }))
                    }
                    placeholder={isBusiness ? "Cargides Group BV" : "Jan Janssen"}
                    className="w-full border-b-2 border-[#e5e5e5] bg-transparent py-4 text-xl focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                    data-testid="input-customer-name"
                />
            </div>
            <div>
                <label className="text-xs uppercase tracking-widest text-[#555]">
                    E-mailadres
                </label>
                <input
                    type="email"
                    value={form.customer_email}
                    onChange={(e) =>
                        setForm((f) => ({ ...f, customer_email: e.target.value.toLowerCase() }))
                    }
                    placeholder="naam@bedrijf.be"
                    className="w-full border-b-2 border-[#e5e5e5] bg-transparent py-4 text-xl focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                    data-testid="input-customer-email"
                    autoComplete="email"
                />
                <p className="text-xs text-[#555] mt-2">
                    De boekingsbevestiging komt naar dit adres.
                </p>
            </div>
            {isBusiness && (
                <div>
                    <label className="text-xs uppercase tracking-widest text-[#555]">
                        Btw-nummer <span className="text-[#ED6A14]">*</span>
                    </label>
                    <input
                        value={form.vat_number}
                        onChange={(e) =>
                            setForm((f) => ({
                                ...f,
                                vat_number: e.target.value.toUpperCase(),
                            }))
                        }
                        placeholder="BE0123.456.789"
                        className="w-full border-b-2 border-[#e5e5e5] bg-transparent py-4 text-xl font-display tracking-wider focus:outline-none focus:border-[#ED6A14] transition-colors uppercase text-[#0A1F44]"
                        data-testid="input-vat-number"
                    />
                    <p className="text-xs text-[#555] mt-2">
                        Verplicht voor de facturatie aan je bedrijf.
                    </p>
                </div>
            )}
        </div>
    );
}

const MIN_PER_CREDIT = 30; // 1 credit = 30 min werk
const minutesToCredits = (min) => Math.ceil(min / MIN_PER_CREDIT);
// Remaining credits: floor so we never overstate availability vs backend
const remainingCredits = (min) => Math.max(0, Math.floor(min / MIN_PER_CREDIT));

const formatDeadline = (iso) =>
    iso
        ? new Date(iso).toLocaleString("nl-BE", {
              weekday: "short",
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
              timeZone: "Europe/Brussels",
          })
        : "";

function getMinUsableDuration(services) {
    if (!services.length) return 15;
    return Math.min(...services.map((s) => s.duration_min));
}

function VehiclesStep({
    vehicles,
    days,
    services,
    updateVehicle,
    addVehicle,
    removeVehicle,
}) {
    if (days.length === 0) {
        return (
            <div className="border border-[#e5e5e5] p-6 text-[#555]">
                Er zijn nog geen wasdagen gepubliceerd. Kom binnenkort terug.
            </div>
        );
    }

    const serviceById = Object.fromEntries(services.map((s) => [s.id, s]));
    // Compute minutes already used by *other* vehicles in this draft for the
    // same (date, slot) so that capacity hints update live while the user types.
    const getUsedMinutes = (date, slot, excludeIndex) => {
        if (!date || !slot) return 0;
        return vehicles.reduce((sum, v, idx) => {
            if (idx === excludeIndex) return sum;
            if (v.date !== date || v.slot !== slot) return sum;
            return sum + (serviceById[v.service_type]?.duration_min || 0);
        }, 0);
    };

    const minServiceDuration = getMinUsableDuration(services);

    return (
        <div className="space-y-6">
            <div
                className="border-l-4 border-[#ED6A14] bg-[#FFF6F0] p-5 text-sm text-[#0A1F44] space-y-3"
                data-testid="practical-info-block"
            >
                <p className="font-semibold uppercase tracking-widest text-xs text-[#ED6A14]">
                    Praktische info
                </p>
                <p>
                    <strong>Uurroosters</strong> —{" "}
                    <strong>Voormiddag</strong>: 08:00 – 12:00 &nbsp;·&nbsp;{" "}
                    <strong>Namiddag</strong>: 13:00 – 17:00
                </p>
                <p className="text-[#555] leading-relaxed">
                    De carwash vindt plaats in het{" "}
                    <strong>parkeergebouw van Westwing Park</strong>. De
                    bestelwagen van Cargides geeft duidelijk aan waar je je kunt
                    aanmelden en waar je wagen wordt opgevangen.
                </p>
                <p className="text-[#555] leading-relaxed">
                    <strong>Sleutels</strong> — je brengt je sleutel bij Cargides
                    binnen wanneer je je wagen afzet en haalt hem terug op
                    binnen het tijdsvenster dat zij aangeven, uiterlijk ongeveer
                    een half uur voor hun vertrek.
                </p>
                <p className="text-[#555] leading-relaxed">
                    We hebben de opstart zo goed mogelijk voorbereid en nemen de
                    nodige voorzorgen, maar willen deze eerste weken vooral ook
                    gebruiken om te leren wat goed werkt en bij te sturen waar
                    nodig. Feedback of suggesties?{" "}
                    <a
                        href="mailto:community@westwingpark.be"
                        className="text-[#ED6A14] font-semibold hover:underline"
                        data-testid="feedback-mailto"
                    >
                        community@westwingpark.be
                    </a>
                </p>
            </div>
            <p className="text-sm text-[#555]">
                Voeg per wagen één lijn toe. Voor een bedrijf kan je gerust 10 wagens
                in één keer inplannen. Onder elke wagen zie je live hoeveel credits
                er nog vrij zijn in de voor- en namiddag (1 credit = exterieur,
                2 credits = interieur & exterieur).
            </p>
            {vehicles.map((v, i) => (
                <VehicleRow
                    key={i}
                    index={i}
                    vehicle={v}
                    days={days}
                    services={services}
                    minServiceDuration={minServiceDuration}
                    getUsedMinutes={getUsedMinutes}
                    onChange={(patch) => updateVehicle(i, patch)}
                    onRemove={vehicles.length > 1 ? () => removeVehicle(i) : null}
                />
            ))}
            <button
                type="button"
                onClick={addVehicle}
                className="inline-flex items-center gap-2 border-2 border-[#ED6A14] text-[#ED6A14] px-5 py-2.5 font-semibold hover:bg-[#ED6A14] hover:text-white transition-colors"
                data-testid="add-vehicle-btn"
            >
                <Plus size={16} /> Wagen toevoegen
            </button>
        </div>
    );
}

function CapacityBar({ label, free, total, active, need, slotKey, vehicleIndex }) {
    // If slot is inactive on this day, render an "off duty" state.
    if (!active || total <= 0) {
        return (
            <div
                className="space-y-1.5 opacity-70"
                data-testid={`capacity-${slotKey}-${vehicleIndex}`}
            >
                <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-[#0A1F44] uppercase tracking-widest">
                        {label}
                    </span>
                    <span
                        className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#eaeaea] text-[#555] border border-[#d5d5d5]"
                        data-testid={`capacity-${slotKey}-${vehicleIndex}-state`}
                    >
                        Niet ingepland
                    </span>
                </div>
                <div className="h-2 w-full bg-[#f0f0f0] overflow-hidden" />
                <div className="text-[11px] text-[#555] italic">
                    We komen deze dag niet langs in de {label.toLowerCase()}.
                </div>
            </div>
        );
    }

    const totalCapCr = Math.max(1, Math.round(total / MIN_PER_CREDIT));
    const freeCr = remainingCredits(free);
    const usedCr = Math.max(0, totalCapCr - freeCr);
    const needCr = need > 0 ? minutesToCredits(need) : 0;
    const pct = Math.min(100, Math.round((usedCr / totalCapCr) * 100));
    const fits = needCr > 0 && freeCr >= needCr;
    const isFull = freeCr < Math.max(1, needCr);

    let stateLabel;
    let stateClass;
    let barClass;
    if (isFull) {
        stateLabel = "VOLZET";
        stateClass = "bg-[#D92D20] text-white";
        barClass = "bg-[#D92D20]";
    } else if (pct >= 80) {
        stateLabel = "Bijna vol";
        stateClass = "bg-[#F79009]/15 text-[#B54708] border border-[#F79009]/40";
        barClass = "bg-[#F79009]";
    } else {
        stateLabel = "Vrij";
        stateClass = "bg-[#12B76A]/15 text-[#067647] border border-[#12B76A]/40";
        barClass = "bg-[#12B76A]";
    }

    return (
        <div
            className="space-y-1.5"
            data-testid={`capacity-${slotKey}-${vehicleIndex}`}
        >
            <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-[#0A1F44] uppercase tracking-widest">
                    {label}
                </span>
                <span
                    className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${stateClass}`}
                    data-testid={`capacity-${slotKey}-${vehicleIndex}-state`}
                >
                    {stateLabel}
                </span>
            </div>
            <div className="h-2 w-full bg-[#f0f0f0] overflow-hidden">
                <div
                    className={`h-full transition-all ${barClass}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="flex items-center justify-between text-[11px] text-[#555] font-mono-ui">
                <span>
                    {freeCr} / {totalCapCr} credits vrij
                </span>
                <span>
                    {fits ? (
                        <span className="text-[#067647]">
                            ✓ past ({needCr} credit{needCr === 1 ? "" : "s"})
                        </span>
                    ) : needCr > 0 ? (
                        <span className="text-[#D92D20]">
                            ✗ {needCr} credit{needCr === 1 ? "" : "s"} nodig
                        </span>
                    ) : (
                        <span>
                            ≈ {freeCr} credit{freeCr === 1 ? "" : "s"} beschikbaar
                        </span>
                    )}
                </span>
            </div>
        </div>
    );
}

function VehicleRow({
    index,
    vehicle,
    days,
    services,
    minServiceDuration,
    getUsedMinutes,
    onChange,
    onRemove,
}) {
    const selectedService = services.find((s) => s.id === vehicle.service_type);
    const need = selectedService?.duration_min || 0;
    const day = days.find((d) => d.date === vehicle.date);

    const morningActive = day ? day.morning_active !== false : true;
    const afternoonActive = day ? day.afternoon_active !== false : true;
    const morningTotal = day ? day.morning_total_min ?? 0 : 0;
    const afternoonTotal = day ? day.afternoon_total_min ?? 0 : 0;

    const morningUsedOther = getUsedMinutes(vehicle.date, "morning", index);
    const afternoonUsedOther = getUsedMinutes(vehicle.date, "afternoon", index);
    const morningFree = day
        ? Math.max(0, day.morning_remaining_min - morningUsedOther)
        : 0;
    const afternoonFree = day
        ? Math.max(0, day.afternoon_remaining_min - afternoonUsedOther)
        : 0;
    const morningFits = morningActive && morningFree >= need;
    const afternoonFits = afternoonActive && afternoonFree >= need;

    const dayIsFull = (d) => {
        const mActive = d.morning_active !== false;
        const aActive = d.afternoon_active !== false;
        if (!mActive && !aActive) return true;
        const mFree = mActive
            ? Math.max(
                  0,
                  d.morning_remaining_min - getUsedMinutes(d.date, "morning", index),
              )
            : 0;
        const aFree = aActive
            ? Math.max(
                  0,
                  d.afternoon_remaining_min - getUsedMinutes(d.date, "afternoon", index),
              )
            : 0;
        const probe = need || minServiceDuration;
        return (!mActive || mFree < probe) && (!aActive || aFree < probe);
    };

    return (
        <div
            className="border border-[#e5e5e5] p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-start"
            data-testid={`vehicle-row-${index}`}
        >
            <div className="md:col-span-3">
                <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                    Nummerplaat
                </label>
                <input
                    value={vehicle.license_plate}
                    onChange={(e) =>
                        onChange({ license_plate: e.target.value.toUpperCase() })
                    }
                    placeholder="1-ABC-234"
                    className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 text-base font-display tracking-wider focus:outline-none focus:border-[#ED6A14] transition-colors uppercase text-[#0A1F44]"
                    data-testid={`vehicle-${index}-plate`}
                />
            </div>
            <div className="md:col-span-3">
                <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                    Dienst
                </label>
                <select
                    value={vehicle.service_type}
                    onChange={(e) => onChange({ service_type: e.target.value })}
                    className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 text-base focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                    data-testid={`vehicle-${index}-service`}
                >
                    {services.map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.id === "exterior" ? "Exterieur" : "Interieur & Exterieur"}{" "}
                            ({s.duration_min} min · €{s.price_eur.toFixed(2).replace(".", ",")})
                        </option>
                    ))}
                </select>
            </div>
            <div className="md:col-span-3">
                <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                    Datum
                </label>
                <select
                    value={vehicle.date}
                    onChange={(e) => onChange({ date: e.target.value, slot: "" })}
                    className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 text-base focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                    data-testid={`vehicle-${index}-date`}
                >
                    <option value="">Kies een dag…</option>
                    {days.map((d) => {
                        const full = dayIsFull(d);
                        return (
                            <option key={d.id} value={d.date}>
                                {new Date(d.date).toLocaleDateString("nl-BE", {
                                    weekday: "short",
                                    day: "2-digit",
                                    month: "short",
                                })}
                                {full
                                    ? " — VOLZET"
                                    : d.booking_closes_at
                                    ? ` · boeken tot ${formatDeadline(d.booking_closes_at)}`
                                    : ""}
                            </option>
                        );
                    })}
                </select>
            </div>
            <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                    Tijdslot
                </label>
                <select
                    value={vehicle.slot}
                    onChange={(e) => onChange({ slot: e.target.value })}
                    disabled={!vehicle.date}
                    className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 text-base focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44] disabled:opacity-50"
                    data-testid={`vehicle-${index}-slot`}
                >
                    <option value="">—</option>
                    <option value="morning" disabled={!morningFits}>
                        Voormiddag (08:00 – 12:00){" "}
                        {!morningActive
                            ? "— Niet ingepland"
                            : morningFits
                            ? `· ${remainingCredits(morningFree)} credits vrij`
                            : "— VOLZET"}
                    </option>
                    <option value="afternoon" disabled={!afternoonFits}>
                        Namiddag (13:00 – 17:00){" "}
                        {!afternoonActive
                            ? "— Niet ingepland"
                            : afternoonFits
                            ? `· ${remainingCredits(afternoonFree)} credits vrij`
                            : "— VOLZET"}
                    </option>
                </select>
            </div>
            <div className="md:col-span-1 flex md:justify-end">
                {onRemove ? (
                    <button
                        type="button"
                        onClick={onRemove}
                        className="text-[#555] hover:text-[#D92D20] transition-colors p-2.5"
                        title="Verwijder deze wagen"
                        data-testid={`vehicle-${index}-remove`}
                    >
                        <Trash2 size={18} />
                    </button>
                ) : null}
            </div>

            {/* Live capacity hints for this date */}
            {vehicle.date && day ? (
                <div className="md:col-span-12 mt-1 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-[#FAFAFA] border border-[#eee] p-4">
                    {day.booking_closes_at && (
                        <div
                            className="sm:col-span-2 inline-flex items-center gap-2 text-xs text-[#0A1F44]"
                            data-testid={`vehicle-${index}-deadline`}
                        >
                            <Clock size={14} className="text-[#ED6A14]" />
                            Boeken voor deze dag kan tot{" "}
                            <strong>{formatDeadline(day.booking_closes_at)}</strong>
                        </div>
                    )}
                    <CapacityBar
                        label="Voormiddag · 08:00 – 12:00"
                        free={morningFree}
                        total={morningTotal}
                        active={morningActive}
                        need={need}
                        slotKey="morning"
                        vehicleIndex={index}
                    />
                    <CapacityBar
                        label="Namiddag · 13:00 – 17:00"
                        free={afternoonFree}
                        total={afternoonTotal}
                        active={afternoonActive}
                        need={need}
                        slotKey="afternoon"
                        vehicleIndex={index}
                    />
                    {(morningUsedOther > 0 || afternoonUsedOther > 0) && (
                        <div
                            className="sm:col-span-2 text-[11px] text-[#555] italic"
                            data-testid={`session-usage-note-${index}`}
                        >
                            ℹ️ Andere wagens in deze boeking gebruiken al{" "}
                            {morningUsedOther > 0 &&
                                `${minutesToCredits(morningUsedOther)} credit${minutesToCredits(morningUsedOther) === 1 ? "" : "s"} in de voormiddag`}
                            {morningUsedOther > 0 && afternoonUsedOther > 0 && " en "}
                            {afternoonUsedOther > 0 &&
                                `${minutesToCredits(afternoonUsedOther)} credit${minutesToCredits(afternoonUsedOther) === 1 ? "" : "s"} in de namiddag`}
                            .
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
}

function SummaryStep({ form, services, total }) {
    const isBusiness = form.customer_type === "business";
    return (
        <div className="space-y-8">
            <div className="border border-[#e5e5e5] p-6">
                <p className="text-xs uppercase tracking-widest text-[#555]">
                    Klantgegevens
                </p>
                <div className="mt-4 space-y-2 text-sm">
                    <Row k={isBusiness ? "Bedrijf" : "Naam"} v={form.customer_name} />
                    <Row k="E-mail" v={form.customer_email} />
                    {isBusiness && <Row k="Btw-nummer" v={form.vat_number} />}
                    <Row k="Type" v={isBusiness ? "Bedrijf" : "Particulier"} />
                </div>
            </div>
            <div className="border border-[#e5e5e5]">
                <p className="text-xs uppercase tracking-widest text-[#555] p-6 pb-3">
                    Wagens ({form.vehicles.length})
                </p>
                <div className="px-6 pb-6">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[#555] text-xs uppercase tracking-widest border-b border-[#e5e5e5]">
                                <th className="py-2 pr-3">Plaat</th>
                                <th className="py-2 pr-3">Dienst</th>
                                <th className="py-2 pr-3">Datum</th>
                                <th className="py-2 pr-3">Slot</th>
                                <th className="py-2 pr-3 text-right">Bedrag</th>
                            </tr>
                        </thead>
                        <tbody>
                            {form.vehicles.map((v, i) => {
                                const s = services.find((x) => x.id === v.service_type);
                                return (
                                    <tr key={i} className="border-b border-[#f0f0f0]">
                                        <td className="py-3 pr-3 font-mono-ui text-[#0A1F44]">
                                            {v.license_plate}
                                        </td>
                                        <td className="py-3 pr-3 text-[#555]">
                                            {s ? s.name : "—"}
                                        </td>
                                        <td className="py-3 pr-3 text-[#555]">
                                            {v.date &&
                                                new Date(v.date).toLocaleDateString("nl-BE", {
                                                    day: "2-digit",
                                                    month: "short",
                                                    year: "numeric",
                                                })}
                                        </td>
                                        <td className="py-3 pr-3 text-[#555]">
                                            {v.slot === "morning"
                                                ? "Voormiddag"
                                                : v.slot === "afternoon"
                                                  ? "Namiddag"
                                                  : "—"}
                                        </td>
                                        <td className="py-3 pr-3 text-right text-[#0A1F44] font-medium">
                                            €{(s?.price_eur || 0).toFixed(2).replace(".", ",")}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={4} className="pt-4 text-right text-xs uppercase tracking-widest text-[#555]">
                                    Totaal (incl. 21% btw)
                                </td>
                                <td className="pt-4 text-right">
                                    <span
                                        className="font-display font-bold text-2xl text-[#0A1F44]"
                                        data-testid="total-amount"
                                    >
                                        €{total.toFixed(2).replace(".", ",")}
                                    </span>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
            <p className="text-sm text-[#555]">
                Geen online betaling — we sturen je achteraf een factuur.
            </p>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex items-baseline justify-between">
            <span className="text-[#555]">{k}</span>
            <span className="font-medium text-[#0A1F44]">{v || "—"}</span>
        </div>
    );
}
